import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
} from '@mui/material';
import { motion } from 'framer-motion';
import salesforceIcon from './image-icons/salesforceIcon.png';
import hubspotIcon from './image-icons/hubspotIcon.png';
import sqlIcon from './image-icons/sqlIcon.png';
import oracleIcon from './image-icons/oracleicon.png';
import jiraIcon from './image-icons/jiraIcon.png';
import driveIcon from './image-icons/driveIcon.png';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    challenges: '',
    message: '',
  });

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  const connectors = [
    { name: 'Salesforce', icon: salesforceIcon, glowColor: 'rgba(0, 112, 210, 0.3)' },
    { name: 'HubSpot', icon: hubspotIcon, glowColor: 'rgba(255, 107, 0, 0.3)' },
    { name: 'SQL', icon: sqlIcon, glowColor: 'rgba(29, 162, 216, 0.3)' },
    { name: 'Oracle', icon: oracleIcon, glowColor: 'rgba(244, 0, 0, 0.3)' },
    { name: 'Jira', icon: jiraIcon, glowColor: null },
    { name: 'Drive', icon: driveIcon, glowColor: null },
  ];

  return (
    <Box
      sx={{
        width: '100vw',
        py: { xs: 8, md: 12 },
        px: { xs: 2, sm: 6, md: 20 },
        background: '#0A0D17',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box
          sx={{
            textAlign: 'center',
            mb: { xs: 6, md: 8 },
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'Sora, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '32px', sm: '40px', md: '48px' },
                lineHeight: '100%',
                letterSpacing: '0%',
                textAlign: 'center',
                background: 'linear-gradient(180deg, #F6F6F7 0%, #7E808F 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                mb: 2,
              }}
            >
              Ready to transform your data stack?
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Typography
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 400,
                fontSize: { xs: '14px', sm: '15px', md: '16px' },
                lineHeight: '160%',
                letterSpacing: '0px',
                textAlign: 'center',
                color: '#FFFFFFCC',
                maxWidth: '800px',
                mx: 'auto',
                px: { xs: 1, sm: 0 },
              }}
            >
              Orchestrate ETL, right-size logs, and migrate databases with confidence—one unified platform.
            </Typography>
          </motion.div>
        </Box>

        {/* Main Content Block */}
        <Paper
          elevation={0}
          sx={{
            background: '#0A0D17',
            borderRadius: '24px',
            p: { xs: 3, sm: 4, md: 5 },
            border: '1px solid rgba(255, 255, 255, 0.1)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Grid container spacing={{ xs: 4, md: 6 }}>
            {/* Left Section - Contact Form */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 600,
                    fontSize: { xs: '24px', sm: '27px', md: '30px' },
                    lineHeight: '100%',
                    color: '#FFFFFF',
                    mb: { xs: 0.5, md: 1 },
                  }}
                >
                  Talk to a Data Expert
                </Typography>

                <Typography
                  sx={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 400,
                    fontSize: { xs: '14px', sm: '15px', md: '16px' },
                    lineHeight: '100%',
                    letterSpacing: '-1%',
                    color: '#FFFFFF',
                    mb: { xs: 3, md: 4 },
                  }}
                >
                  Whether you&apos;re evaluating ETL, logs, or migration workflows—our team is here to help.
                </Typography>

                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        placeholder="Name"
                        value={formData.name}
                        onChange={handleInputChange('name')}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'transparent',
                            borderRadius: '8px',
                            '& fieldset': {
                              borderColor: '#FFFFFF33',
                              borderWidth: '1px',
                            },
                            '&:hover fieldset': {
                              borderColor: '#FFFFFF33',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#FFFFFF33',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: '#FFFFFF',
                            fontFamily: 'Manrope, sans-serif',
                            fontSize: { xs: '14px', md: '17px' },
                          },
                          '& .MuiInputBase-input::placeholder': {
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 400,
                            fontSize: { xs: '14px', sm: '14.5px', md: '15px' },
                            lineHeight: '100%',
                            letterSpacing: '-1%',
                            color: '#FFFFFF99',
                            opacity: 1,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="email"
                        placeholder="Email"
                        value={formData.email}
                        onChange={handleInputChange('email')}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'transparent',
                            borderRadius: '8px',
                            '& fieldset': {
                              borderColor: '#FFFFFF33',
                              borderWidth: '1px',
                            },
                            '&:hover fieldset': {
                              borderColor: '#FFFFFF33',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#FFFFFF33',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: '#FFFFFF',
                            fontFamily: 'Manrope, sans-serif',
                            fontSize: { xs: '14px', md: '14px' },
                          },
                          '& .MuiInputBase-input::placeholder': {
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 400,
                            fontSize: { xs: '14px', sm: '14.5px', md: '15px' },
                            lineHeight: '100%',
                            letterSpacing: '-1%',
                            color: '#FFFFFF99',
                            opacity: 1,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        placeholder="Company"
                        value={formData.company}
                        onChange={handleInputChange('company')}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'transparent',
                            borderRadius: '8px',
                            '& fieldset': {
                              borderColor: '#FFFFFF33',
                              borderWidth: '1px',
                            },
                            '&:hover fieldset': {
                              borderColor: '#FFFFFF33',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#FFFFFF33',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: '#FFFFFF',
                            fontFamily: 'Manrope, sans-serif',
                            fontSize: { xs: '14px', md: '14px' },
                          },
                          '& .MuiInputBase-input::placeholder': {
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 400,
                            fontSize: { xs: '14px', sm: '14.5px', md: '15px' },
                            lineHeight: '100%',
                            letterSpacing: '-1%',
                            color: '#FFFFFF99',
                            opacity: 1,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        placeholder="Current Challenges"
                        value={formData.challenges}
                        onChange={handleInputChange('challenges')}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'transparent',
                            borderRadius: '8px',
                            '& fieldset': {
                              borderColor: '#FFFFFF33',
                              borderWidth: '1px',
                            },
                            '&:hover fieldset': {
                              borderColor: '#FFFFFF33',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#FFFFFF33',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: '#FFFFFF',
                            fontFamily: 'Manrope, sans-serif',
                            fontSize: { xs: '14px', md: '14px' },
                          },
                          '& .MuiInputBase-input::placeholder': {
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 400,
                            fontSize: { xs: '14px', sm: '14.5px', md: '15px' },
                            lineHeight: '100%',
                            letterSpacing: '-1%',
                            color: '#FFFFFF99',
                            opacity: 1,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Message (Optional)"
                        value={formData.message}
                        onChange={handleInputChange('message')}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'transparent',
                            borderRadius: '8px',
                            '& fieldset': {
                              borderColor: '#FFFFFF33',
                              borderWidth: '1px',
                            },
                            '&:hover fieldset': {
                              borderColor: '#FFFFFF33',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#FFFFFF33',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: '#FFFFFF',
                            fontFamily: 'Manrope, sans-serif',
                            fontSize: { xs: '14px', md: '14px' },
                          },
                          '& .MuiInputBase-input::placeholder': {
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 400,
                            fontSize: { xs: '14px', sm: '14.5px', md: '15px' },
                            lineHeight: '100%',
                            letterSpacing: '-1%',
                            color: '#FFFFFF99',
                            opacity: 1,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <Button
                          type="submit"
                          variant="contained"
                          sx={{
                            flex: 1,
                            fontFamily: 'Manrope, sans-serif',
                            fontWeight: 600,
                            fontSize: { xs: '14px', md: '16px' },
                            py: { xs: 1.25, md: 1.5 },
                            borderRadius: '8px',
                            backgroundColor: '#FFFFFF',
                            color: '#1F2937',
                            textTransform: 'none',
                            '&:hover': {
                              backgroundColor: '#F3F4F6',
                            },
                          }}
                        >
                          Get in Touch
                        </Button>
                        <Button
                          variant="contained"
                          sx={{
                            flex: 1,
                            fontFamily: 'Manrope, sans-serif',
                            fontWeight: 600,
                            fontSize: { xs: '14px', md: '16px' },
                            py: { xs: 1.25, md: 1.5 },
                            borderRadius: '8px',
                            background: 'linear-gradient(90deg, #8225FA 0%, #434BFB 100%)',
                            color: '#FFFFFF',
                            textTransform: 'none',
                            '&:hover': {
                              background: 'linear-gradient(90deg, #6D1ED9 0%, #3A3FE8 100%)',
                            },
                          }}
                        >
                          Schedule a Demo →
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </motion.div>
            </Grid>

            {/* Right Section - Connectors */}
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end' }}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Box
                  sx={{
                    background: 'linear-gradient(152.63deg, #B7AEFF -65.47%, #EFF6FF 124.76%)',
                    border: '1.24px solid #434657',
                    borderRadius: '12px',
                    p: { xs: 2, sm: 2.5, md: 4 },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: 600,
                      fontSize: { xs: '22px', sm: '24px', md: '26px' },
                      lineHeight: '100%',
                      letterSpacing: '-3%',
                      textAlign: 'center',
                      color: '#282D45',
                      mb: { xs: 3, md: 4 },
                    }}
                  >
                    Connectors
                  </Typography>

                  <Grid container spacing={{ xs: 0.5, sm: 1 }}>
                  {connectors.map((connector, index) => (
                    <Grid item xs={4} key={index}>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                      >
                      <Box
                        sx={{
                          borderRadius: '12px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: { xs: 1, sm: 1.5, md: 2 },
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: connector.glowColor
                              ? `0 8px 16px rgba(0, 0, 0, 0.15), 0 0 30px ${connector.glowColor}`
                              : '0 8px 16px rgba(0, 0, 0, 0.15)',
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={connector.icon}
                          alt={connector.name}
                          sx={{
                            width: { xs: '60px', sm: '70px', md: '80px', lg: '100px' },
                            height: { xs: '60px', sm: '70px', md: '80px', lg: '100px' },
                            objectFit: 'contain',
                            mb: 1,
                          }}
                        />
                        <Typography
                          sx={{
                            fontFamily: 'Manrope, sans-serif',
                            fontWeight: 500,
                            fontSize: { xs: '12px', md: '14px' },
                            color: '#4B5563',
                            textAlign: 'center',
                          }}
                        >
                          {connector.name}
                        </Typography>
                      </Box>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default ContactSection;

